<template>
  <b-container fluid>
    <Breadcrumbs
      v-if="guard1000.substation"
      :project="guard1000.substation.project"
      :substation="guard1000.substation"
      :guard1000="guard1000"
    />
    <loader watch="feederDiagram" :is-loading="isLoading">
      <b-alert variant="danger" :show="Boolean(error)"> {{ errorMessage }}</b-alert>
      <div class="form-view">
        <b-row no-gutters class="align-items-baseline substation__sort">
          <b-col>
            <div class="pb-3">{{ title }}</div>
          </b-col>
          <b-col align-self="end">
            <v-select
              v-model="selectedDiagram"
              :searchable="false"
              :disabled="disabledSelect"
              :options="diagramTypes"
              class="custom-select"
            />
          </b-col>
        </b-row>
        <b-form-file
          v-model="file"
          plain
          accept="application/pdf"
          no-multiple
          :state="Boolean(file)"
        />
        <b-row v-if="diagrams.length" class="mt-5">
          <b-col v-for="diagram in filteredDiagrams" :key="diagram.id.$oid" md="auto">
            <b-img src="~@/assets/images/pdf.png" alt="pdf" class="pdf-image" />
            <b-img
              src="~@/assets/images/delete_pdf.png"
              class="pdf-image__remove"
              alt="pdf_remove"
              @click="showRemoveModal(diagram)"
            />
            <p>{{ mappedDiagrams[diagram['diagram-type']] }}</p>
          </b-col>
        </b-row>
        <b-form-group class="form-group__buttons">
          <router-link
            :to="generateFaultPanelDiagramsParams()"
            class="btn btn-link font-weight-bold"
          >
            <span v-if="filteredDiagrams.length === 2">Go Back</span>
            <span v-else>Cancel</span>
          </router-link>
          <b-button
            type="submit"
            variant="rounded"
            :disabled="!convertedFile || filteredDiagrams.length === 2"
            @click.prevent="uploadFile"
          >
            Upload
            <Icon name="save" />
          </b-button>
        </b-form-group>
      </div>
    </loader>
    <b-modal
      v-model="showModal"
      title="Removing diagram"
      ok-title="Delete"
      cancel-title="Cancel"
      ok-variant="rounded"
      cancel-variant="link"
      @ok="removeDiagram"
      @cancel="resetDeletion"
    >
      Are you sure you want to delete given diagram ?
    </b-modal>
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  DIAGRAM_TYPES,
  EIGHT_MEGABYTES,
  mappedDiagrams,
  NO_UPLOAD_POSSIBLE_MESSAGE,
  ERRORS
} from '@/components/shared/fault-consts';
import Breadcrumbs from '@/components/shared/breadcrumbs';

export default {
  name: 'Guard1000FeederDiagram',
  components: { Breadcrumbs },
  data() {
    return {
      mappedDiagrams,
      selectedForDeletion: null,
      showModal: false,
      disabledSelect: false,
      diagramTypes: DIAGRAM_TYPES,
      selectedDiagram: DIAGRAM_TYPES[0],
      title: 'Upload PDF',
      isLoading: false,
      file: null,
      convertedFile: null,
      error: null
    };
  },
  computed: {
    ...mapState({
      guard1000: state => state.guard1000.data
    }),
    ...mapGetters({
      diagrams: 'guard1000/feederDiagrams'
    }),
    errorMessage() {
      if (typeof this.error === 'string') return this.error;
      return ERRORS.DEFAULT;
    },
    filteredDiagrams() {
      const feederNumber = this.$route.params.feederNumber;

      return this.diagrams(feederNumber - 1);
    }
  },
  watch: {
    file(val) {
      if (!val) return;

      this.isLoading = true;

      if (val.size >= EIGHT_MEGABYTES) {
        this.file = null;
        this.isLoading = false;

        return this.handleError(ERRORS.FILE_LIMIT);
      }
      const Reader = new FileReader();

      Reader.readAsDataURL(val);
      Reader.onload = () => {
        this.convertedFile = Reader.result;
        this.handleError(false);
        this.isLoading = false;
      };
      Reader.onerror = error => {
        this.handleError(error);
        this.isLoading = false;
      };
    },
    filteredDiagrams(val) {
      if (val.length === 2) {
        this.diagramTypes = NO_UPLOAD_POSSIBLE_MESSAGE;
        this.selectedDiagram = this.diagramTypes[0];
        this.disabledSelect = true;
      } else if (this.filteredDiagrams.length) {
        this.diagramTypes = [
          DIAGRAM_TYPES.find(el => el.ref !== this.filteredDiagrams[0]['diagram-type'])
        ];
        this.selectedDiagram = this.diagramTypes[0];
      }
    }
  },
  created() {
    const { projectCode, substationCode, externalGuardId } = this.$route.params;

    const data = {
      projectCode,
      substationCode,
      externalGuardId
    };
    this.dispatchFetchGuard10000(data);
  },
  methods: {
    ...mapActions({
      dispatchFetchGuard10000: 'guard1000/fetchGuard1000',
      dispatchCreateDiagram: 'feederDiagram/createDiagram',
      dispatchRemoveDiagram: 'feederDiagram/removeDiagram'
    }),
    ...mapMutations({
      commitResetGuard1000: 'guard1000/resetStore'
    }),
    async removeDiagram() {
      this.handleError(false);

      const { projectCode, substationCode, externalGuardId, feederNumber } = this.$route.params;
      const {
        'instrument-type': instrumentType,
        'diagram-type': diagramType,
        id: { $oid: diagramId }
      } = this.selectedForDeletion;

      const feederDiagramParams = {
        projectCode,
        substationCode,
        externalInstrumentId: externalGuardId,
        instrumentType,
        diagramType,
        feederNumber,
        diagramId
      };
      const isRemovedError = await this.dispatchRemoveDiagram(feederDiagramParams);

      if (isRemovedError) {
        return this.handleError(isRemovedError);
      }
      return location.reload();
    },
    async uploadFile() {
      this.handleError(false);
      const { projectCode, substationCode, externalGuardId, feederNumber } = this.$route.params;

      const feederDiagramParams = {
        projectCode,
        substationCode,
        externalInstrumentId: externalGuardId,
        instrumentType: 'guard1000',
        diagramType: this.selectedDiagram.ref,
        feederNumber,
        feederDiagramFile: this.convertedFile
      };

      const isAddedError = await this.dispatchCreateDiagram(feederDiagramParams);

      if (isAddedError) {
        return this.handleError(isAddedError);
      }
      return location.reload();
    },
    generateFaultPanelDiagramsParams() {
      const { projectCode, substationCode, externalGuardId } = this.$route.params;

      return {
        name: 'Guard1000PanelDefault',
        params: {
          projectCode,
          substationCode,
          externalGuardId
        }
      };
    },
    showRemoveModal(diagram) {
      this.showModal = !this.showModal;
      this.selectedForDeletion = diagram;
    },
    resetDeletion() {
      this.selectedForDeletion = null;
    },
    handleError(value) {
      this.error = value;
    }
  }
};
</script>
